import { FormHelperText } from '@mui/material';
import { DateOrTimeView, DateTimePicker, DateTimePickerProps } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { Control, Controller, FieldError, FieldErrorsImpl, FieldValues, Merge, Path } from 'react-hook-form';
import { DATE_TIME_FORMAT, DATE_FORMAT } from '../../constants';

const BASE_VIEWS = ['year', 'month', 'day'];
const TIME_VIEWS = ['hours', 'minutes'];

const DATE_TIME_CONFIG = {
  withTime: {
    views: [...BASE_VIEWS, ...TIME_VIEWS],
    format: DATE_TIME_FORMAT,
  },
  withoutTime: {
    views: BASE_VIEWS,
    format: DATE_FORMAT,
  },
} as const;

type DateTimeSelectProps<
  T extends FieldValues,
  TTransformedValues extends FieldValues | undefined = T,
> = DateTimePickerProps & {
  name: Path<T>;
  control: Control<T, unknown, TTransformedValues>;
  label?: string;
  error?: FieldError | Merge<FieldError, FieldErrorsImpl<Dayjs>>;
  disableTimePicker?: boolean;
  disablePast?: boolean;
  required?: boolean;
  fullWidth?: boolean;
};

const DateTimeSelect = <T extends FieldValues, TTransformedValues extends FieldValues | undefined = T>({
  name,
  control,
  error,
  label,
  disablePast,
  disableTimePicker,
  required,
  fullWidth,
  ...rest
}: DateTimeSelectProps<T, TTransformedValues>) => {
  const configKey = disableTimePicker ? 'withoutTime' : 'withTime';
  const { views, format } = DATE_TIME_CONFIG[configKey];

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange } }) => {
        return (
          <DateTimePicker
            label={label || 'Date'}
            onChange={onChange}
            value={value ? dayjs(value) : null}
            ampm={false}
            disablePast={disablePast}
            views={views as DateOrTimeView[]}
            format={format}
            slotProps={{
              textField: {
                sx: {
                  width: fullWidth ? '100%' : 'auto',
                },

                error: !!error,
                required,
                helperText: <FormHelperText sx={{ ml: 0 }}>{error?.message as string}</FormHelperText>,

                slotProps: {
                  formHelperText: {
                    component: 'div',
                    sx: {
                      marginTop: 0,
                    },
                  },
                },
              },
            }}
            {...rest}
          />
        );
      }}
    />
  );
};
export default DateTimeSelect;
